import type {
  Color,
  InventoryHistory,
  InventoryItem,
  ProjectListItem,
  Purchase,
  RecentCalibration,
} from './api-contracts'

export type { Color, InventoryHistory, InventoryItem, ProjectListItem, Purchase, RecentCalibration }

export interface ToolRecord {
  id: number
  name: string
  price: number | null
  note: string | null
  created_at: string
}

export interface TopConsumed {
  color_code: string
  hex: string | null
  series: string | null
  project_count: number
  total_quantity: number
  total_qty: number
}

export interface InventoryWarning {
  color_code: string
  quantity: number
  stock: number
  hex: string | null
  series: string | null
  total_consumed: number
  used_in_projects: number
  avg_per_project: number | null
  estimated_projects_left: number | null
}

export interface DraftShortage {
  color_code: string
  hex: string | null
  series: string | null
  planned_quantity: number
  total_quantity: number
  current_quantity: number
  stock: number
  shortage_quantity: number
  project_count: number
  priority_score: number
  priority_label: string
}

export interface ProjectInputItem {
  color_code: string
  actual_color_code: string
  quantity: number
}

export interface ParsedColorLines {
  items: ProjectInputItem[]
  errors: string[]
}

export function money(value: number): string {
  return Number.isFinite(value) ? new Intl.NumberFormat('zh-CN', { style: 'currency', currency: 'CNY' }).format(value) : '—'
}

export function number(value: number): string {
  return Number.isFinite(value) ? new Intl.NumberFormat('zh-CN').format(value) : '—'
}

export function shortDate(value: string | null | undefined): string {
  if (!value) return '未设日期'
  const dateOnly = value.slice(0, 10)
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateOnly)
  if (match) {
    const [, year, month, day] = match
    const date = new Date(Number(year), Number(month) - 1, Number(day), 12)
    if (date.getFullYear() === Number(year) && date.getMonth() === Number(month) - 1 && date.getDate() === Number(day)) return new Intl.DateTimeFormat('zh-CN', { dateStyle: 'medium' }).format(date)
  }
  return value
}

export function parseColorLines(text: string): ProjectInputItem[] {
  return parseColorLinesStrict(text).items
}

export function parseColorLinesStrict(text: string, knownCodes?: ReadonlySet<string>): ParsedColorLines {
  const merged = new Map<string, number>()
  const errors: string[] = []
  for (const [index, raw] of text.split(/\r?\n/).entries()) {
    if (!raw.trim()) continue
    const [rawCode, rawQuantity] = raw.split(/[,，\s]+/)
    const code = rawCode?.trim().toUpperCase() ?? ''
    const quantity = Number(rawQuantity)
    if (!code || rawQuantity == null || !Number.isInteger(quantity) || quantity <= 0) {
      errors.push(`第 ${index + 1} 行格式错误，应为“色号,正整数颗数”`)
      continue
    }
    if (knownCodes && !knownCodes.has(code)) {
      errors.push(`第 ${index + 1} 行色号不存在：${code}`)
      continue
    }
    merged.set(code, (merged.get(code) ?? 0) + quantity)
  }
  return {
    items: [...merged].map(([color_code, quantity]) => ({ color_code, actual_color_code: color_code, quantity })),
    errors,
  }
}

export function validateImageFile(file: File): string | null {
  const extension = file.name.split('.').pop()?.toLowerCase()
  if (extension === 'heic' || extension === 'heif' || /hei[cf]/i.test(file.type)) {
    return `${file.name} 是 HEIF/HEIC，请先转换为 JPG、PNG 或 WebP。`
  }
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
    return `${file.name} 不是支持的 JPG、PNG 或 WebP 图片。`
  }
  return null
}

export const RECOMMENDED_REPLACEMENTS: Readonly<Record<string, string>> = {
  A1:'A3',A2:'A3',A5:'A4',A8:'A4',A9:'A10',A12:'G3',A14:'A7',A15:'A4',A16:'A3',A17:'A11',A18:'G2',A19:'E3',A20:'A4',A21:'A11',A22:'A3',A23:'G1',A24:'A3',A25:'A11',A26:'A6',
  B1:'B18',B2:'B5',B4:'B5',B6:'B3',B9:'B12',B11:'B12',B13:'B14',B15:'B12',B16:'B20',B21:'B7',B22:'B12',B23:'B12',B24:'B20',B25:'B8',B26:'G7',B27:'B17',B28:'B3',B29:'B14',B30:'B20',B31:'B14',B32:'B17',
  C1:'B20',C4:'C10',C9:'C8',C12:'C16',C14:'D16',C15:'C11',C17:'C10',C18:'C16',C19:'C11',C20:'C5',C21:'C13',C22:'B10',C23:'C3',C24:'C6',C25:'B10',C26:'C5',C27:'D16',C28:'C13',C29:'C16',
  D1:'D11',D4:'D3',D5:'D13',D10:'D15',D17:'D11',D22:'D15',D23:'D8',D24:'D2',D25:'D15',D26:'D12',E6:'E5',E9:'E3',E10:'E5',E11:'E1',E14:'E1',E15:'E2',E16:'H2',E17:'E2',E18:'E8',E19:'E2',E20:'E2',E21:'G7',E22:'D12',E23:'D12',E24:'D8',
  F1:'A10',F2:'F5',F3:'F13',F4:'F5',F6:'F10',F9:'E3',F11:'G8',F12:'F5',F14:'E12',F15:'F5',F16:'E1',F17:'G3',F18:'G5',F19:'F5',F20:'G3',F21:'E3',F22:'E1',F23:'F13',F24:'E1',F25:'F13',G4:'G7',G6:'G5',G10:'G13',G11:'A11',G12:'G3',G14:'G7',G15:'H2',G16:'E1',G17:'G8',G18:'G1',G19:'G5',G20:'F10',G21:'G13',
  H6:'H7',H8:'E2',H9:'H2',H10:'D8',H11:'H3',H12:'H2',H13:'G3',H14:'H3',H15:'H3',H16:'H7',H17:'H2',H18:'H2',H19:'H2',H20:'H3',H21:'H2',H22:'H2',H23:'H3',M1:'H3',M2:'H5',M3:'H5',M4:'H3',M5:'H3',M6:'H3',M7:'H3',M8:'H4',M9:'H4',M10:'D18',M11:'D18',M12:'G8',M13:'G7',M14:'F10',M15:'H4',
}
